import { useForm } from 'react-hook-form';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useDriveStatus, useConfigureDrive, useForceDriveSync } from '../../hooks';
import { Cloud, RefreshCw } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

interface DriveStatus {
  drive_backup_enabled: boolean;
  drive_backup_root_id: string | null;
  drive_service_account_email: string | null;
  drive_last_synced_at: string | null;
  drive_quota_used_bytes: number;
  drive_health: 'ok' | 'degraded' | 'failed' | 'disabled';
  drive_consecutive_failures: number;
}

/** Content-only version used by the Settings page (tab: drive). */
export function OwnerDriveConfigContent() {
  const { register, handleSubmit, getValues } = useForm<{ drive_root_id: string }>();
  const { data: status, isLoading: statusLoading } = useDriveStatus() as { data: DriveStatus | undefined; isLoading: boolean };
  const configure = useConfigureDrive();
  const forceSync = useForceDriveSync();
  const toast = useToast();
  const confirm = useConfirm();

  const usedGb = status ? (status.drive_quota_used_bytes / 1024 ** 3).toFixed(2) : '0';

  async function onSubmit(data: { drive_root_id: string }) {
    try {
      await configure.mutateAsync(data);
      toast.success('ตั้งค่าสำเร็จ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleTestAccess() {
    const drive_root_id = getValues('drive_root_id');
    if (!drive_root_id) { toast.error('กรุณากรอก Folder ID ก่อน'); return; }
    try {
      await configure.mutateAsync({ drive_root_id });
      toast.success('ทดสอบการเข้าถึงสำเร็จ');
    } catch (e) {
      toast.error(`ไม่สามารถเข้าถึงได้: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  async function handleForceSync() {
    if (!(await confirm({ title: 'Sync Drive ทันที?' }))) return;
    try {
      await forceSync.mutateAsync(undefined);
      toast.success('เริ่ม sync แล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <>
      {/* Status Card */}
      {!statusLoading && status && (
        <div className="card p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">สถานะ Drive</p>
            <span className={`badge-${status.drive_health === 'ok' ? 'success' : status.drive_health === 'failed' ? 'danger' : 'warn'}`}>
              {status.drive_health}
            </span>
          </div>
          <p className="text-sm text-gray-700">Sync ล่าสุด: {status.drive_last_synced_at ? new Date(status.drive_last_synced_at).toLocaleString('th-TH') : 'ยังไม่เคย'}</p>
          <p className="text-sm text-gray-700">พื้นที่ใช้: {usedGb} GB</p>
          {status.drive_consecutive_failures > 0 && (
            <p className="text-sm text-red-600 mt-1">ล้มเหลวติดต่อกัน: {status.drive_consecutive_failures} ครั้ง</p>
          )}
          {status.drive_service_account_email && (
            <p className="text-xs text-gray-600 mt-2">
              <Cloud className="w-3 h-3 inline mr-1" />
              Service Account: <code className="bg-gray-100 px-1 text-xs">{status.drive_service_account_email}</code>
            </p>
          )}
        </div>
      )}

      {/* Instruction */}
      <div className="card p-4 mb-4 bg-blue-50 border-blue-200">
        <p className="text-sm font-medium mb-2">ขั้นตอน:</p>
        <ol className="text-sm space-y-1 list-decimal list-inside text-gray-700">
          <li>เปิด Google Drive สร้างโฟลเดอร์ใหม่ เช่น "PayrollBackup"</li>
          <li>แชร์โฟลเดอร์ให้ Service Account ด้วยสิทธิ์ "Editor"</li>
          <li>คัดลอก Folder ID จาก URL (ส่วนหลัง /folders/)</li>
          <li>วางในช่องด้านล่าง แล้วกด "บันทึก"</li>
        </ol>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="card p-4 space-y-3 mb-4">
        <div>
          <label className="label">Drive Folder ID</label>
          <input
            {...register('drive_root_id', { required: true, minLength: 10 })}
            className="input font-mono text-sm"
            placeholder="1abc...XYZ"
            defaultValue={status?.drive_backup_root_id ?? ''}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleTestAccess}
            disabled={configure.isPending}
            className="btn-secondary"
          >
            ทดสอบการเข้าถึง
          </button>
          <button type="submit" disabled={configure.isPending} className="btn-primary">
            {configure.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </form>

      {/* Force Sync */}
      <button
        onClick={handleForceSync}
        disabled={forceSync.isPending}
        className="btn-secondary w-full"
      >
        <RefreshCw className={`w-4 h-4 ${forceSync.isPending ? 'animate-spin' : ''}`} />
        {forceSync.isPending ? 'กำลัง sync...' : 'Force Sync Drive ทันที'}
      </button>
    </>
  );
}

/** Standalone page kept for legacy /owner/drive (now redirects via Settings). */
export function OwnerDriveConfig() {
  return (
    <AdminLayout title="ตั้งค่า Google Drive">
      <OwnerDriveConfigContent />
    </AdminLayout>
  );
}
